import { useState, type Dispatch, type SetStateAction } from 'react';

import { MIN_EMPLOYEES_FOR_QUOTES } from '../../constants/quotes';
import type {
  ICommercialAddon,
  ICensusValidationResult,
  IQuickCensusError,
  IQuotePriceCalculationResult
} from '../../interfaces/quotes';
import Quotes from '../../services/api/quotes/quotes';
import { type AgeBandBreakdownRow, computeAvgAge, groupSummaryIntoBands } from '../../utils/quotes/quotes';

export type CensusQuotaModalStep =
  | 'upload'
  | 'validating'
  | 'processing'
  | 'success'
  | 'error-data-many'
  | 'error-mismatch'
  | 'error-mismatch-over'
  | 'error-service';
type MismatchOverOption = 'update' | 'upload-different';
interface UseCensusFlowArgs {
  brokerId: string;
  employeesSize: string | number | null | undefined;
  minAge?: number;
  maxAge?: number;
  addon?: ICommercialAddon | null;
  onUpdateHeadcount?: (next: number) => void;
}

interface UseCensusFlowResult {
  step: CensusQuotaModalStep;
  setStep: Dispatch<SetStateAction<CensusQuotaModalStep>>;
  file: File | null;
  setFile: Dispatch<SetStateAction<File | null>>;
  slowWarning: boolean;
  setSlowWarning: Dispatch<SetStateAction<boolean>>;
  mismatchCount: number;
  mismatchOverOption: MismatchOverOption | null;
  setMismatchOverOption: Dispatch<SetStateAction<MismatchOverOption | null>>;
  validationResult: ICensusValidationResult | null;
  priceCalculationResult: IQuotePriceCalculationResult | null;
  setPriceCalculationResult: Dispatch<SetStateAction<IQuotePriceCalculationResult | null>>;
  avgAge: number | null;
  ageBandBreakdown: AgeBandBreakdownRow[];
  censusErrors: IQuickCensusError[];
  handleFileSelect: (f: File) => void;
  handleUpload: () => void;
  handleRetry: () => void;
  handleDownloadTemplate: () => Promise<void>;
  handleMismatchOverContinue: () => void;
}

export function useCensusFlow({
  brokerId,
  employeesSize,
  minAge,
  maxAge,
  addon,
  onUpdateHeadcount
}: UseCensusFlowArgs): UseCensusFlowResult {
  const [step, setStep] = useState<CensusQuotaModalStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ICensusValidationResult | null>(null);
  const [slowWarning, setSlowWarning] = useState(false);
  const [priceCalculationResult, setPriceCalculationResult] = useState<IQuotePriceCalculationResult | null>(null);
  const [mismatchOverOption, setMismatchOverOption] = useState<'update' | 'upload-different' | null>(null);

  const expectedEmployeesSize = Number.parseInt(String(employeesSize), 10) || 0;

  function handleFileSelect(f: File) {
    setFile(f);
  }

  async function runValidation(targetFile: File) {
    setStep('validating');
    setSlowWarning(false);
    setValidationResult(null);
    setPriceCalculationResult(null);
    setMismatchOverOption(null);

    try {
      const result = await Quotes.validateQuickCensus(brokerId, targetFile, minAge, maxAge);
      const { errors = [], records = [], summary = [], ...rest } = result ?? {};
      const safeResult = { ...rest, errors, records, summary };
      setValidationResult(safeResult);
      if (errors.length > 0) {
        setStep('error-data-many');
        return;
      } else if (records.length > 0 && records.length < MIN_EMPLOYEES_FOR_QUOTES) {
        setStep('error-mismatch');
        return;
      } else if (records.length > 0 && records.length !== expectedEmployeesSize) {
        setMismatchOverOption(null);
        setStep('error-mismatch-over');
        return;
      } else if (safeResult.valid && errors.length === 0) {
        setStep('processing');
        return;
      }
      setStep('error-service');
    } catch {
      setStep('error-service');
    }
  }

  function handleUpload() {
    if (!file) return;
    runValidation(file);
  }

  function handleRetry() {
    if (!file) return;
    runValidation(file);
  }

  function handleMismatchOverContinue() {
    if (mismatchOverOption === 'update') {
      onUpdateHeadcount?.(validationResult?.records.length ?? 0);
      setStep('processing');
    } else if (mismatchOverOption === 'upload-different') {
      setFile(null);
      setStep('upload');
    }
  }

  async function handleDownloadTemplate() {
    try {
      const { url } = await Quotes.getQuickCensusTemplate(brokerId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // Silently fail — template download is non-critical
    }
  }

  const censusErrors = validationResult?.errors ?? [];

  const mismatchCount = validationResult?.records.length ?? 0;

  const avgAge = validationResult ? computeAvgAge(validationResult.summary) : null;

  const ageBandBreakdown: AgeBandBreakdownRow[] = validationResult?.summary
    ? groupSummaryIntoBands(validationResult.summary, addon)
    : [];

  return {
    step,
    setStep,
    file,
    setFile,
    slowWarning,
    setSlowWarning,
    mismatchCount,
    mismatchOverOption,
    setMismatchOverOption,
    validationResult,
    priceCalculationResult,
    setPriceCalculationResult,
    avgAge,
    ageBandBreakdown,
    censusErrors,
    handleFileSelect,
    handleUpload,
    handleRetry,
    handleDownloadTemplate,
    handleMismatchOverContinue
  };
}
